// Browser fingerprinting utilities
// Collects various browser characteristics to create a unique identifier

/**
 * Creates a script that can be injected into the browser to generate a fingerprint
 */
export function createFingerprintScript() {
  return `
    /**
     * Store fingerprint in multiple locations to survive cache clearing
     */
    function storeFingerprint(fingerprint) {
      try {
        // Store in localStorage
        localStorage.setItem('fp_id', fingerprint);
        
        // Store in sessionStorage
        sessionStorage.setItem('fp_id', fingerprint);
        
        // Store in cookies with long expiration
        const expiryDate = new Date();
        expiryDate.setFullYear(expiryDate.getFullYear() + 1);
        document.cookie = \`fp_id=\${fingerprint}; expires=\${expiryDate.toUTCString()}; path=/; SameSite=Strict\`;
        
        // Store in IndexedDB if available
        if (window.indexedDB) {
          const request = indexedDB.open('fingerprint_db', 1);
          
          request.onupgradeneeded = function(event) {
            const db = event.target.result;
            if (!db.objectStoreNames.contains('fingerprints')) {
              db.createObjectStore('fingerprints', { keyPath: 'id' });
            }
          };
          
          request.onsuccess = function(event) {
            try {
              const db = event.target.result;
              // Check if the object store exists before attempting to use it
              if (db.objectStoreNames.contains('fingerprints')) {
                const transaction = db.transaction(['fingerprints'], 'readwrite');
                const store = transaction.objectStore('fingerprints');
                store.put({ id: 'user', value: fingerprint });
              } else {
                console.log('Object store not found, recreating...');
                // Close the database and reopen it to create the object store
                db.close();
                const reopenRequest = indexedDB.open('fingerprint_db', db.version + 1);
                reopenRequest.onupgradeneeded = function(event) {
                  const db = event.target.result;
                  if (!db.objectStoreNames.contains('fingerprints')) {
                    db.createObjectStore('fingerprints', { keyPath: 'id' });
                  }
                };
                reopenRequest.onsuccess = function(event) {
                  const db = event.target.result;
                  const transaction = db.transaction(['fingerprints'], 'readwrite');
                  const store = transaction.objectStore('fingerprints');
                  store.put({ id: 'user', value: fingerprint });
                };
              }
            } catch (e) {
              console.error('Error storing fingerprint in IndexedDB:', e);
            }
          };
        }
      } catch (e) {
        console.error('Error storing fingerprint:', e);
      }
    }

    /**
     * Retrieve stored fingerprint from any available storage
     */
    function getStoredFingerprint() {
      try {
        // Try localStorage
        const localFingerprint = localStorage.getItem('fp_id');
        if (localFingerprint) return localFingerprint;
        
        // Try sessionStorage
        const sessionFingerprint = sessionStorage.getItem('fp_id');
        if (sessionFingerprint) return sessionFingerprint;
        
        // Try cookies
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].trim();
          if (cookie.startsWith('fp_id=')) {
            return cookie.substring(6);
          }
        }
      } catch (e) {
        console.error('Error retrieving fingerprint:', e);
      }
      return null;
    }

    /**
     * Detect if browser is in incognito/private mode
     */
    async function detectIncognito() {
      try {
        // Method 1: FileSystem API (Chrome)
        if (navigator.userAgent.includes('Chrome')) {
          const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
          if (fs) {
            return new Promise(resolve => {
              fs(window.TEMPORARY, 100,
                () => resolve(false), // FileSystem available, not incognito
                () => resolve(true)   // FileSystem unavailable, likely incognito
              );
            });
          }
        }
        
        // Method 2: Storage quota (Firefox, Safari)
        if ('storage' in navigator && 'estimate' in navigator.storage) {
          const {quota} = await navigator.storage.estimate();
          if (quota < 120000000) {
            return true; // Less than 120MB quota usually means incognito
          }
        }
        
        return false;
      } catch (e) {
        console.error('Incognito detection error:', e);
        return false;
      }
    }

    async function getBrowserFingerprint() {
      try {
        // Try to get stored fingerprint first (to maintain consistency)
        const storedFingerprint = getStoredFingerprint();
        if (storedFingerprint && typeof storedFingerprint === 'string' && storedFingerprint.length > 10) {
          console.log('Using stored fingerprint');
          return storedFingerprint;
        }
        
        // Clear invalid fingerprint if it exists but is invalid
        if (storedFingerprint && (typeof storedFingerprint !== 'string' || storedFingerprint.length <= 10)) {
          console.log('Clearing invalid stored fingerprint:', storedFingerprint);
          try {
            localStorage.removeItem('fp_id');
            sessionStorage.removeItem('fp_id');
            document.cookie = 'fp_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
          } catch (e) {
            console.error('Error clearing invalid fingerprint:', e);
          }
        }

        // Get IP address from external IP API service (more reliable)
        let ipHash = '';
        let ipAddress = '';
        try {
          // First try our own API endpoint
          const ipResponse = await fetch('/api/trial/register', {
            method: 'HEAD', // Use HEAD to just get headers without body
            credentials: 'same-origin'
          });
          
          // The server will include the IP in the response headers
          let clientIP = ipResponse.headers.get('X-Client-IP') ||
                         ipResponse.headers.get('CF-Connecting-IP') ||
                         ipResponse.headers.get('X-Forwarded-For');
          
          // If we couldn't get the IP from our API, try an external IP API
          if (!clientIP) {
            console.log('Could not get IP from internal API, trying external IP API');
            try {
              // Use a public IP API service with CORS support
              const externalIPResponse = await fetch('https://api.ipify.org?format=json', {
                method: 'GET',
                mode: 'cors'
              });
              
              if (externalIPResponse.ok) {
                const ipData = await externalIPResponse.json();
                clientIP = ipData.ip;
                console.log('Got IP from external API:', clientIP);
              }
            } catch (externalError) {
              console.error('Error getting IP from external API:', externalError);
              
              // Try another IP API as fallback
              try {
                const fallbackIPResponse = await fetch('https://api.ipapi.com/api/check?access_key=YOUR_ACCESS_KEY', {
                  method: 'GET',
                  mode: 'cors'
                });
                
                if (fallbackIPResponse.ok) {
                  const ipData = await fallbackIPResponse.json();
                  clientIP = ipData.ip;
                  console.log('Got IP from fallback API:', clientIP);
                }
              } catch (fallbackError) {
                console.error('Error getting IP from fallback API:', fallbackError);
              }
            }
          }
          
          if (clientIP) {
            ipAddress = clientIP; // Store the raw IP address
            const encoder = new TextEncoder();
            const ipBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(clientIP));
            ipHash = Array.from(new Uint8Array(ipBuffer))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('').substring(0, 16);
            
            console.log('Successfully obtained IP address for fingerprinting');
          } else {
            console.error('Failed to obtain IP address from any source');
          }
          
          // Also include a session key as backup
          const sessionKey = sessionStorage.getItem('session_key') ||
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
          
          // Store the session key if it doesn't exist
          if (!sessionStorage.getItem('session_key')) {
            sessionStorage.setItem('session_key', sessionKey);
          }
        } catch (e) {
          console.error('IP fingerprinting failed:', e);
          
          // Fallback to session key if IP fetch fails
          try {
            const sessionKey = sessionStorage.getItem('session_key') ||
              Math.random().toString(36).substring(2, 15) +
              Math.random().toString(36).substring(2, 15);
            
            if (!sessionStorage.getItem('session_key')) {
              sessionStorage.setItem('session_key', sessionKey);
            }
            
            const encoder = new TextEncoder();
            const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(sessionKey));
            ipHash = Array.from(new Uint8Array(buffer))
              .map(b => b.toString(16).padStart(2, '0'))
              .join('').substring(0, 16);
          } catch (e) {
            console.error('Session key generation failed:', e);
          }
        }

        // Detect incognito mode with multiple methods for better accuracy
        const isIncognito = await detectIncognito();
        console.log('Incognito mode detected:', isIncognito);
        
        // If in incognito mode, prioritize IP in the fingerprint
        const components = {
          // Put IP hash first if in incognito mode to give it more weight in the fingerprint
          ...(isIncognito ? { priorityIpHash: ipHash } : {}),
          ipHash: ipHash,
          userAgent: navigator.userAgent,
          language: navigator.language,
          colorDepth: screen.colorDepth,
          deviceMemory: navigator.deviceMemory,
          hardwareConcurrency: navigator.hardwareConcurrency,
          screenResolution: [screen.width, screen.height],
          availableScreenResolution: [screen.availWidth, screen.availHeight],
          timezoneOffset: new Date().getTimezoneOffset(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          sessionStorage: !!window.sessionStorage,
          localStorage: !!window.localStorage,
          indexedDb: !!window.indexedDB,
          platform: navigator.platform,
          vendor: navigator.vendor,
          isIncognito: isIncognito,
          plugins: Array.from(navigator.plugins || []).map(p => p.name).join('|'),
          doNotTrack: navigator.doNotTrack || window.doNotTrack,
          cookiesEnabled: navigator.cookieEnabled,
        };

        // Add canvas fingerprint
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 200;
        canvas.height = 50;
        
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125,1,62,20);
        ctx.fillStyle = '#069';
        ctx.fillText('Fingerprint', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Fingerprint', 4, 17);
        
        components.canvasFingerprint = canvas.toDataURL();

        // Add WebGL fingerprint
        try {
          const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
          if (gl) {
            // Try to get debug renderer info (more accurate)
            const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
            if (debugInfo) {
              components.webglVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
              components.webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            } else {
              components.webglVendor = gl.getParameter(gl.VENDOR);
              components.webglRenderer = gl.getParameter(gl.RENDERER);
            }
            
            const exts = gl.getSupportedExtensions();
            components.webglExtensions = exts ? exts.join('|') : '';
          }
        } catch (e) {
          console.error('WebGL fingerprint failed:', e);
        }

        // Hash all components
        const encoder = new TextEncoder();
        const data = encoder.encode(JSON.stringify(components));
        const hashBuffer = await crypto.subtle.digest('SHA-256', data);
        
        const fingerprint = Array.from(new Uint8Array(hashBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('');
        
        // Store the fingerprint in multiple locations
        storeFingerprint(fingerprint);
        
        return fingerprint;
      } catch (error) {
        console.error('Fingerprint generation error:', error);
        
        // Try to get stored fingerprint as fallback
        const storedFingerprint = getStoredFingerprint();
        if (storedFingerprint) {
          return storedFingerprint;
        }
        
        // Last resort fallback - ensure we return a string that can handle substring operations
        return (navigator.userAgent || 'unknown-fingerprint-fallback');
      }
    }

    getBrowserFingerprint().then(fingerprint => {
      const headers = new Headers();
      headers.append('X-Browser-Fingerprint', fingerprint);
      return fingerprint;
    });
  `;
}

/**
 * Generate browser fingerprint with additional protections against cache clearing and incognito mode
 */
export async function getBrowserFingerprint() {
  try {
    // Try to get stored fingerprint first (to maintain consistency)
    const storedFingerprint = getStoredFingerprint();
    if (storedFingerprint && typeof storedFingerprint === 'string' && storedFingerprint.length > 10) {
      console.log('Using stored fingerprint');
      return storedFingerprint;
    }
    
    // Clear invalid fingerprint if it exists but is invalid
    if (storedFingerprint && (typeof storedFingerprint !== 'string' || storedFingerprint.length <= 10)) {
      console.log('Clearing invalid stored fingerprint:', storedFingerprint);
      try {
        localStorage.removeItem('fp_id');
        sessionStorage.removeItem('fp_id');
        document.cookie = 'fp_id=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      } catch (e) {
        console.error('Error clearing invalid fingerprint:', e);
      }
    }

    // Get IP address from external IP API service (more reliable)
    let ipHash = '';
    let ipAddress = '';
    try {
      // First try our own API endpoint
      const ipResponse = await fetch('/api/trial/register', {
        method: 'HEAD', // Use HEAD to just get headers without body
        credentials: 'same-origin'
      });
      
      // The server will include the IP in the response headers
      let clientIP = ipResponse.headers.get('X-Client-IP') ||
                     ipResponse.headers.get('CF-Connecting-IP') ||
                     ipResponse.headers.get('X-Forwarded-For');
      
      // If we couldn't get the IP from our API, try an external IP API
      if (!clientIP) {
        console.log('Could not get IP from internal API, trying external IP API');
        try {
          // Use a public IP API service with CORS support
          const externalIPResponse = await fetch('https://api.ipify.org?format=json', {
            method: 'GET',
            mode: 'cors'
          });
          
          if (externalIPResponse.ok) {
            const ipData = await externalIPResponse.json();
            clientIP = ipData.ip;
            console.log('Got IP from external API:', clientIP);
          }
        } catch (externalError) {
          console.error('Error getting IP from external API:', externalError);
          
          // Try another IP API as fallback
          try {
            const fallbackIPResponse = await fetch('https://api.ipapi.com/api/check?access_key=YOUR_ACCESS_KEY', {
              method: 'GET',
              mode: 'cors'
            });
            
            if (fallbackIPResponse.ok) {
              const ipData = await fallbackIPResponse.json();
              clientIP = ipData.ip;
              console.log('Got IP from fallback API:', clientIP);
            }
          } catch (fallbackError) {
            console.error('Error getting IP from fallback API:', fallbackError);
          }
        }
      }
      
      if (clientIP) {
        ipAddress = clientIP; // Store the raw IP address
        const encoder = new TextEncoder();
        const ipBuffer = await crypto.subtle.digest('SHA-256', encoder.encode(clientIP));
        ipHash = Array.from(new Uint8Array(ipBuffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('').substring(0, 16);
        
        console.log('Successfully obtained IP address for fingerprinting');
      } else {
        console.error('Failed to obtain IP address from any source');
      }
      
      // Also include a session key as backup
      const sessionKey = sessionStorage.getItem('session_key') ||
        Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
      
      // Store the session key if it doesn't exist
      if (!sessionStorage.getItem('session_key')) {
        sessionStorage.setItem('session_key', sessionKey);
      }
    } catch (e) {
      console.error('IP fingerprinting failed:', e);
      
      // Fallback to session key if IP fetch fails
      try {
        const sessionKey = sessionStorage.getItem('session_key') ||
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        
        if (!sessionStorage.getItem('session_key')) {
          sessionStorage.setItem('session_key', sessionKey);
        }
        
        const encoder = new TextEncoder();
        const buffer = await crypto.subtle.digest('SHA-256', encoder.encode(sessionKey));
        ipHash = Array.from(new Uint8Array(buffer))
          .map(b => b.toString(16).padStart(2, '0'))
          .join('').substring(0, 16);
      } catch (e) {
        console.error('Session key generation failed:', e);
      }
    }

    // Detect incognito mode with multiple methods for better accuracy
    const isIncognito = await detectIncognito();
    console.log('Incognito mode detected:', isIncognito);
    
    // If in incognito mode, prioritize IP in the fingerprint
    const components = {
      // Put IP hash first if in incognito mode to give it more weight in the fingerprint
      ...(isIncognito ? { priorityIpHash: ipHash } : {}),
      ipHash: ipHash,
      userAgent: navigator.userAgent,
      language: navigator.language,
      colorDepth: screen.colorDepth,
      deviceMemory: navigator.deviceMemory,
      hardwareConcurrency: navigator.hardwareConcurrency,
      screenResolution: [screen.width, screen.height],
      availableScreenResolution: [screen.availWidth, screen.availHeight],
      timezoneOffset: new Date().getTimezoneOffset(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      sessionStorage: !!window.sessionStorage,
      localStorage: !!window.localStorage,
      indexedDb: !!window.indexedDB,
      platform: navigator.platform,
      vendor: navigator.vendor,
      isIncognito: isIncognito,
      plugins: Array.from(navigator.plugins || []).map(p => p.name).join('|'),
      doNotTrack: navigator.doNotTrack || window.doNotTrack,
      cookiesEnabled: navigator.cookieEnabled,
      cpuClass: navigator.cpuClass,
      oscpu: navigator.oscpu,
      buildID: navigator.buildID,
    };

    // Add canvas fingerprint
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 200;
    canvas.height = 50;
    
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125,1,62,20);
    ctx.fillStyle = '#069';
    ctx.fillText('Fingerprint', 2, 15);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fillText('Fingerprint', 4, 17);
    
    components.canvasFingerprint = canvas.toDataURL();

    // Add WebGL fingerprint
    try {
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (gl) {
        // Try to get debug renderer info (more accurate)
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
          components.webglVendor = gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL);
          components.webglRenderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        } else {
          components.webglVendor = gl.getParameter(gl.VENDOR);
          components.webglRenderer = gl.getParameter(gl.RENDERER);
        }
        
        const exts = gl.getSupportedExtensions();
        components.webglExtensions = exts ? exts.join('|') : '';
        
        // Add additional WebGL parameters
        components.webglParams = {
          alpha: gl.getParameter(gl.ALPHA_BITS),
          depth: gl.getParameter(gl.DEPTH_BITS),
          stencil: gl.getParameter(gl.STENCIL_BITS),
          antialias: gl.getContextAttributes().antialias,
          maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE),
          maxViewportDims: gl.getParameter(gl.MAX_VIEWPORT_DIMS),
        };
      }
    } catch (e) {
      console.error('WebGL fingerprint failed:', e);
    }

    // Add audio fingerprint
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const analyser = audioContext.createAnalyser();
      components.audioFingerprint = {
        sampleRate: audioContext.sampleRate,
        channelCount: analyser.channelCount,
        fftSize: analyser.fftSize,
        timeConstant: analyser.timeConstant,
      };
      audioContext.close();
    } catch (e) {
      console.error('Audio fingerprint failed:', e);
    }

    // Hash all components
    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(components));
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    const fingerprint = Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
    
    // Store the fingerprint in multiple locations
    storeFingerprint(fingerprint);
    
    return fingerprint;
  } catch (error) {
    console.error('Fingerprint generation error:', error);
    
    // Try to get stored fingerprint as fallback
    const storedFingerprint = getStoredFingerprint();
    if (storedFingerprint) {
      return storedFingerprint;
    }
    
    // Last resort fallback - ensure we return a string that can handle substring operations
    return (navigator.userAgent || 'unknown-fingerprint-fallback');
  }
}

/**
 * Store fingerprint in multiple locations to survive cache clearing
 */
function storeFingerprint(fingerprint) {
  try {
    // Store in localStorage
    localStorage.setItem('fp_id', fingerprint);
    
    // Store in sessionStorage
    sessionStorage.setItem('fp_id', fingerprint);
    
    // Store in cookies with long expiration
    const expiryDate = new Date();
    expiryDate.setFullYear(expiryDate.getFullYear() + 1);
    document.cookie = `fp_id=${fingerprint}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Strict`;
    
    // Store in IndexedDB if available
    if (window.indexedDB) {
      const request = indexedDB.open('fingerprint_db', 1);
      
      request.onupgradeneeded = function(event) {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('fingerprints')) {
          db.createObjectStore('fingerprints', { keyPath: 'id' });
        }
      };
      
      request.onsuccess = function(event) {
        try {
          const db = event.target.result;
          // Check if the object store exists before attempting to use it
          if (db.objectStoreNames.contains('fingerprints')) {
            const transaction = db.transaction(['fingerprints'], 'readwrite');
            const store = transaction.objectStore('fingerprints');
            store.put({ id: 'user', value: fingerprint });
          } else {
            console.log('Object store not found, recreating...');
            // Close the database and reopen it to create the object store
            db.close();
            const reopenRequest = indexedDB.open('fingerprint_db', db.version + 1);
            reopenRequest.onupgradeneeded = function(event) {
              const db = event.target.result;
              if (!db.objectStoreNames.contains('fingerprints')) {
                db.createObjectStore('fingerprints', { keyPath: 'id' });
              }
            };
            reopenRequest.onsuccess = function(event) {
              const db = event.target.result;
              const transaction = db.transaction(['fingerprints'], 'readwrite');
              const store = transaction.objectStore('fingerprints');
              store.put({ id: 'user', value: fingerprint });
            };
          }
        } catch (e) {
          console.error('Error storing fingerprint in IndexedDB:', e);
        }
      };
    }
  } catch (e) {
    console.error('Error storing fingerprint:', e);
  }
}

/**
 * Retrieve stored fingerprint from any available storage
 */
function getStoredFingerprint() {
  try {
    // Try localStorage
    const localFingerprint = localStorage.getItem('fp_id');
    if (localFingerprint) return localFingerprint;
    
    // Try sessionStorage
    const sessionFingerprint = sessionStorage.getItem('fp_id');
    if (sessionFingerprint) return sessionFingerprint;
    
    // Try cookies
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith('fp_id=')) {
        return cookie.substring(6);
      }
    }
    
    // Try IndexedDB
    return new Promise((resolve) => {
      if (!window.indexedDB) {
        resolve(null);
        return;
      }
      
      const request = indexedDB.open('fingerprint_db', 1);
      
      request.onsuccess = function(event) {
        const db = event.target.result;
        try {
          const transaction = db.transaction(['fingerprints'], 'readonly');
          const store = transaction.objectStore('fingerprints');
          const getRequest = store.get('user');
          
          getRequest.onsuccess = function() {
            if (getRequest.result) {
              resolve(getRequest.result.value);
            } else {
              resolve(null);
            }
          };
          
          getRequest.onerror = function() {
            resolve(null);
          };
        } catch (e) {
          resolve(null);
        }
      };
      
      request.onerror = function() {
        resolve(null);
      };
      
      // Set a timeout in case the IndexedDB operation hangs
      setTimeout(() => resolve(null), 1000);
    });
  } catch (e) {
    console.error('Error retrieving fingerprint:', e);
    return null;
  }
}

/**
 * Detect if browser is in incognito/private mode
 */
export async function detectIncognito() {
  try {
    // Use multiple detection methods and combine results for better accuracy
    let incognitoScore = 0;
    let totalTests = 0;
    
    // Method 1: Check if cookies are enabled but localStorage is not
    try {
      totalTests++;
      if (navigator.cookieEnabled && !window.localStorage) {
        incognitoScore++;
      }
    } catch (e) {
      incognitoScore++;
    }
    
    // Method 2: FileSystem API (Chrome)
    if (navigator.userAgent.includes('Chrome')) {
      const fs = window.RequestFileSystem || window.webkitRequestFileSystem;
      if (fs) {
        totalTests++;
        try {
          const result = await new Promise(resolve => {
            fs(window.TEMPORARY, 100,
              () => resolve(false), // FileSystem available, not incognito
              () => resolve(true)   // FileSystem unavailable, likely incognito
            );
          });
          if (result) incognitoScore++;
        } catch (e) {
          incognitoScore++;
        }
      }
    }
    
    // Method 3: Storage quota (Firefox, Safari)
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      totalTests++;
      try {
        const {quota} = await navigator.storage.estimate();
        if (quota < 120000000) {
          incognitoScore++; // Less than 120MB quota usually means incognito
        }
      } catch (e) {
        incognitoScore++;
      }
    }
    
    // Method 4: IndexedDB test
    totalTests++;
    try {
      const db = window.indexedDB.open('test');
      const result = await new Promise(resolve => {
        db.onerror = () => resolve(true);  // Error opening DB, likely incognito
        db.onsuccess = () => {
          try {
            db.result.close();
          } catch (e) {}
          resolve(false); // Successfully opened DB, not incognito
        };
        // Set a timeout in case the operation hangs
        setTimeout(() => resolve(true), 500);
      });
      if (result) incognitoScore++;
    } catch (e) {
      incognitoScore++;
    }
    
    // Method 5: Check if sessionStorage is available but doesn't persist
    totalTests++;
    try {
      const testKey = 'incognito_test';
      sessionStorage.setItem(testKey, '1');
      const value = sessionStorage.getItem(testKey);
      sessionStorage.removeItem(testKey);
      if (value !== '1') {
        incognitoScore++;
      }
    } catch (e) {
      incognitoScore++;
    }
    
    // Calculate the final result - if more than 50% of tests indicate incognito mode
    const isIncognito = totalTests > 0 && (incognitoScore / totalTests) >= 0.5;
    console.log(`Incognito detection: ${incognitoScore}/${totalTests} tests indicate incognito mode`);
    return isIncognito;
  } catch (e) {
    console.error('Incognito detection error:', e);
    return true; // Assume incognito on error to be safe
  }
}